import {
  Box,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';

// Wellbeing Insights Skeleton
export const WellbeingInsightsSkeleton: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card
      sx={{
        mb: 3,
        borderRadius: 8,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        p: { xs: 2, md: 3 },
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header with title and buttons */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={250} height={28} />
          </Box>
          <Box display="flex" gap={1}>
            <Skeleton
              variant="rectangular"
              width={120}
              height={36}
              sx={{ borderRadius: 10, bgcolor: '#60a5fa' }}
            />
            <Skeleton
              variant="rectangular"
              width={120}
              height={36}
              sx={{ borderRadius: 10, bgcolor: 'transparent', border: '2px solid #60a5fa' }}
            />
          </Box>
        </Box>

        {/* Score circle and assessment info */}
        <Box display="flex" alignItems="flex-start" gap={3} mb={4}>
          <Box position="relative" display="inline-flex">
            <Skeleton
              variant="circular"
              width={80}
              height={80}
              sx={{ bgcolor: 'rgba(255, 140, 0, 0.2)' }}
            />
            <Box
              position="absolute"
              top="50%"
              left="50%"
              sx={{ transform: 'translate(-50%, -50%)' }}
            >
              <Skeleton variant="text" width={30} height={24} />
              <Skeleton variant="text" width={35} height={16} />
            </Box>
          </Box>
          <Box flex={1}>
            <Skeleton variant="text" width={150} height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="90%" height={16} />
          </Box>
        </Box>

        {/* Key insights section */}
        <Box mb={4}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Skeleton variant="circular" width={16} height={16} />
            <Skeleton variant="text" width={120} height={20} />
          </Box>

          {/* Insight paragraphs */}
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 2, p: 2 }}>
            <Skeleton variant="text" width="100%" height={18} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="95%" height={18} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="88%" height={18} sx={{ mb: 2 }} />

            <Skeleton variant="text" width="98%" height={18} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="92%" height={18} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="85%" height={18} />
          </Box>
        </Box>

        {/* Trend chart section */}
        <Box mb={4}>
          <Box display="flex" alignItems="center" gap={1} mb={3}>
            <Skeleton variant="rectangular" width={16} height={16} />
            <Skeleton variant="text" width={180} height={20} />
          </Box>

          {/* Chart area */}
          <Box sx={{ height: 200, position: 'relative', mb: 2 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />

            {/* Simulate chart line */}
            <Box
              sx={{
                position: 'absolute',
                top: '60%',
                left: 0,
                right: 0,
                height: 2,
                background:
                  'linear-gradient(90deg, transparent 0%, #6366f1 20%, #6366f1 80%, transparent 100%)',
              }}
            />

            {/* Chart points */}
            {[15, 35, 55, 75, 85].map((left, index) => (
              <Skeleton
                key={index}
                variant="circular"
                width={8}
                height={8}
                sx={{
                  position: 'absolute',
                  top: '58%',
                  left: `${left}%`,
                  bgcolor: '#6366f1',
                }}
              />
            ))}
          </Box>

          {/* Chart x-axis labels */}
          <Box display="flex" justifyContent="space-between" px={1}>
            {['Jan 5', 'Jan 12', 'Jan 19', 'Jan 26', 'Feb 2'].map((label, index) => (
              <Skeleton key={index} variant="text" width={40} height={16} />
            ))}
          </Box>
        </Box>

        {/*  boosters section */}
        <Box mb={4}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Skeleton variant="rectangular" width={16} height={16} />
            <Skeleton variant="text" width={160} height={20} />
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {[
              { width: 120, text: 'Home environment' },
              { width: 80, text: 'Family' },
              { width: 140, text: 'Meaningful connection' },
              { width: 100, text: 'Team culture' },
              { width: 90, text: 'Role clarity' },
            ].map((item, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                width={item.width}
                height={32}
                sx={{
                  borderRadius: 4,
                  bgcolor: 'rgba(34, 197, 94, 0.2)',
                  mb: 1,
                }}
              />
            ))}
          </Stack>
        </Box>

        {/*  drainers section */}
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Skeleton variant="rectangular" width={16} height={16} />
            <Skeleton variant="text" width={160} height={20} />
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {[
              { width: 110, text: 'Work-life balance' },
              { width: 100, text: 'Sleep quality' },
              { width: 110, text: 'Emergency fund' },
              { width: 120, text: 'Handling anxiety' },
              { width: 110, text: 'Physical fitness' },
            ].map((item, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                width={item.width}
                height={32}
                sx={{
                  borderRadius: 4,
                  bgcolor: 'rgba(239, 68, 68, 0.2)',
                  mb: 1,
                }}
              />
            ))}
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
};

// Current Priorities Skeleton
export const CurrentPrioritiesSkeleton: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Card
      sx={{
        mb: 3,
        borderRadius: 8,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        p: { xs: 2, md: 3 },
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Header with title and edit button */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Skeleton variant="text" width={200} height={28} />
          <Skeleton variant="rectangular" width={60} height={36} sx={{ borderRadius: 3 }} />
        </Box>

        {/* Description */}
        <Skeleton variant="text" width="85%" height={18} sx={{ mb: 4 }} />

        {/* Priority cards */}
        <Grid container spacing={2}>
          {[
            {
              category: 'Mental',
              title: 'Stress Resilience',
              color: 'rgba(34, 197, 94, 0.2)',
              iconColor: '#22c55e',
            },
            {
              category: 'Mental',
              title: 'Managing Emotions',
              color: 'rgba(34, 197, 94, 0.2)',
              iconColor: '#22c55e',
            },
            {
              category: 'Financial',
              title: 'Spending',
              color: 'rgba(249, 115, 22, 0.2)',
              iconColor: '#f97316',
            },
          ].map((priority, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.8)',
                  border: '1px solid rgba(0,0,0,0.1)',
                  position: 'relative',
                  minHeight: 120,
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  {/* Close button */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                    }}
                  >
                    <Skeleton variant="circular" width={20} height={20} />
                  </Box>

                  {/* Category with icon */}
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Skeleton
                      variant="circular"
                      width={16}
                      height={16}
                      sx={{ bgcolor: priority.iconColor }}
                    />
                    <Skeleton variant="text" width={80} height={16} />
                    <Skeleton variant="text" width={20} height={16} />
                    <Skeleton variant="text" width={60} height={16} />
                  </Box>

                  {/* Priority title */}
                  <Skeleton variant="text" width="85%" height={24} sx={{ fontWeight: 600 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

// Wellbeing Plan Skeleton
export const WellbeingPlanSkeleton: React.FC = () => (
  <Card sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
    <CardContent sx={{ p: { xs: 2, md: 4 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
      </Box>

      {[...Array(3)].map((_, index) => (
        <Card key={index} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box flex={1}>
                <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="90%" height={20} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="60%" height={20} />
              </Box>
              <Skeleton
                variant="rectangular"
                width={80}
                height={32}
                sx={{ borderRadius: 1, ml: 2, bgcolor: '#beaaf3' }}
              />
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" gap={1}>
                <Skeleton
                  variant="rectangular"
                  width={60}
                  height={24}
                  sx={{ borderRadius: 1, bgcolor: '#04bc00' }}
                />
                <Skeleton
                  variant="rectangular"
                  width={80}
                  height={24}
                  sx={{ borderRadius: 1, bgcolor: '#04bc00' }}
                />
              </Box>
              <Skeleton variant="text" width={80} height={16} />
            </Box>
          </CardContent>
        </Card>
      ))}

      <Box textAlign="center" mt={3}>
        <Skeleton
          variant="rectangular"
          width={150}
          height={40}
          sx={{ mx: 'auto', borderRadius: 4, bgcolor: '#60a5fa' }}
        />
      </Box>
    </CardContent>
  </Card>
);
